import React from 'react';

import type { Role, UserDto } from '../../api/types';
import { getInitialsFromName } from '../../domain/users/display';
import { useAppSelector } from '../../store/hooks';
import { RoleBadge } from '../members/RoleBadge';

import './SelectionModals.css';

type MemberSelectModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (telegramId: string) => void;
  excludeIds?: string[];
};

export function MemberSelectModal({
  isOpen,
  onClose,
  onSelect,
  excludeIds = [],
}: MemberSelectModalProps) {
  const users = useAppSelector((s) => s.entities.users.items);
  const [search, setSearch] = React.useState('');

  const filteredUsers = React.useMemo(() => {
    return users
      .filter((u) => !excludeIds.includes(u.telegramId))
      .filter((u) => {
        if (!search) return true;
        const q = search.toLowerCase();
        const fullName = `${u.firstName} ${u.lastName}`.toLowerCase();
        const username = u.username?.toLowerCase() || '';
        return fullName.includes(q) || username.includes(q);
      });
  }, [users, excludeIds, search]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleSelect = (user: UserDto) => {
    onSelect(user.telegramId);
    onClose();
  };

  React.useEffect(() => {
    if (isOpen) {
      setSearch('');
    }
  }, [isOpen]);

  return (
    <div className={`modal-overlay ${isOpen ? 'show' : ''}`} onClick={handleOverlayClick}>
      <div className="modal-container">
        <button className="modal-close" onClick={onClose}>
          ×
        </button>

        <div className="modal-header">
          <h2 className="modal-title">
            <span>📜</span>
            Список искателей приключений
          </h2>
          <p className="modal-subtitle">Выберите участника для приглашения</p>
        </div>

        <div className="modal-search">
          <span className="modal-search-icon">🔍</span>
          <input
            type="text"
            className="modal-search-input"
            placeholder="Поиск по имени..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="modal-content">
          {filteredUsers.length === 0 ? (
            <div className="modal-empty-state">
              <div className="modal-empty-icon">👥</div>
              <div className="modal-empty-text">
                {search ? 'Участники не найдены' : 'Нет доступных участников'}
              </div>
            </div>
          ) : (
            <div className="members-list">
              {filteredUsers.map((user) => {
                const fullName = `${user.firstName} ${user.lastName}`.trim();
                const initials = getInitialsFromName(fullName);
                const isValidUrl =
                  user.photoUrl &&
                  (user.photoUrl.startsWith('http') || user.photoUrl.startsWith('/'));

                return (
                  <div
                    key={user.telegramId}
                    className="member-item member-item--selectable"
                    onClick={() => handleSelect(user)}
                  >
                    <div className="mi-avatar-wrap">
                      <div className="mi-avatar">
                        {isValidUrl ? (
                          <img src={user.photoUrl} alt={fullName} />
                        ) : (
                          <span className="member-avatar-placeholder">{initials}</span>
                        )}
                      </div>
                    </div>
                    <RoleBadge role={user.role as Role} hasClubKey={user.hasClubKey} />
                    <div className="mi-content">
                      <span className="member-name">{fullName}</span>
                      <div className="member-select-icon">✓</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
